import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  FormControl,
  AbstractControl,
  ReactiveFormsModule,
} from '@angular/forms';
// Services
import { SharedService } from '../../shared/services/shared.service';
// components
import { ToggleSwitchComponent } from '../../shared/components/toggle-switch/toggle-switch.component';
// functions
import { autoFillSequence } from '../../shared/utils/autofill.utils';
import { getLastFiveFFLs, numberOnly } from '../../shared/functions';
// Models
import {
  BoundBookEntry,
  BulkAcquisitionItem,
  FFL,
  Firearm,
  Note,
} from '../../shared/models/general';
import {
  Answers,
  ContactTypes,
  DateFormats,
  ModalID,
  ModalType,
  Questions,
  StatusModalIndicator,
  DateTimePickerConfig,
  ComponentNames,
  InventoryTypes,
} from '../../shared/models/constants';
import { AccountingService } from '../../accounting/services/accounting.service';
import { Customer, ProductServiceItem } from '../../shared/models/accounting';
// Third Party
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
import { NgSelectModule } from '@ng-select/ng-select';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-acquisition-bulk',
  templateUrl: './acquisition-bulk.component.html',
  styleUrls: ['./acquisition-bulk.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, TooltipModule, ToggleSwitchComponent, NgClass],
})
export class AcquisitionBulkComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  itemsForm: FormGroup;
  itemsToAcquire: FormArray;
  selected_ffl: FFL = new FFL();
  acquisition_date: string = format(new Date(), DateFormats.M_D_Y_H_M_AM);
  isToggleOn: boolean = false; // false = FFL, true = customer
  customers: Customer[] = [];
  importing_items: boolean = false;
  acquisition_items: BulkAcquisitionItem[] = []; // items to acquire

  bsConfig: Partial<BsDatepickerConfig>;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    public sharedService: SharedService,
    public accountingService: AccountingService,
  ) {
    this.itemsForm = this.fb.group({
      notes: [''],
      acquisition_date: ['', Validators.required],
      received_from: ['', Validators.required],
      receiver_type: ['FFL', Validators.required],
      items_to_acquire: this.fb.array([]),
    });
    this.itemsToAcquire = this.itemsForm.get('items_to_acquire') as FormArray;
    this.bsConfig = Object.assign({}, DateTimePickerConfig);
    // ffl_selected$
    this.subscriptions.add(
      this.sharedService.ffl_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.selected_ffl = response as FFL;
          this.itemsForm.get('received_from').patchValue(this.selected_ffl);
        }),
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          console.warn(response);
          if (
            response.answer === Answers.yes &&
            response.question === Questions.delete_serial
          ) {
            this.removeSerial(
              response.optional_details.itemIndex,
              response.optional_details.serialIndex,
            );
          } else if (
            response.answer === Answers.yes &&
            response.question === Questions.delete_bulk_serial_item
          ) {
            this.removeItem(response.optional_details.itemIndex);
          } else if (
            response.answer === Answers.yes &&
            response.question === Questions.close_bulk_acquisition
          ) {
            this.router.navigate(['accounting-expense']);
          }
        }),
    );
    // contacts_retrieved$
    this.subscriptions.add(
      this.accountingService.customer_list_retrieved$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.customers = this.accountingService.customers.filter(
            (customer) => customer.contact_type === 'Customer',
          );
        }),
    );
    // edit customer$
    this.subscriptions.add(
      this.accountingService.customer_contact_to_edit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          console.warn(response);
          this.accountingService.editCustomer(response.customer as Customer);
        }),
    );
    // product_service_to_add$
    this.subscriptions.add(
      this.accountingService.product_service_to_add$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (response) => {
          await this.accountingService.addNewProductService(response.item);
          this.sharedService.raiseSuccessToast('Product added successfully');
          // patch description
          this.items
            .at(response.rowIndex)
            .patchValue({ description: response.item.description });
          // check if firearm is in firearm list
          const firearm_found = this.sharedService.firearm_list.filter(
            (firearm) => firearm.firearm_upc === response.item.upc,
          )[0];
          if (!firearm_found) {
            const firearm = new Firearm();
            firearm.firearm_upc = response.item.upc;
            this.sharedService.addEditFirearmClick(
              ModalType.add,
              firearm,
              response.rowIndex,
              ComponentNames.bulk_acquisition,
            );
          }
        }),
    );
    // firearm_to_add$
    this.subscriptions.add(
      this.sharedService.firearm_to_add$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (firearm) => {
          console.warn(firearm);
          await this.sharedService.addFirearmToAvailabilityList(
            firearm.form_values,
          );
          this.sharedService.raiseSuccessToast('Firearm added successfully');
        }),
    );
    // this.sharedService.clear_bulk_acquisition_rows$
    this.subscriptions.add(
      this.sharedService.clear_bulk_acquisition_rows$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((index) => {
          this.removeAllSerialsForGivenUpc(index);
          this.items.at(index).patchValue({ upc: '', description: '' });
        }),
    );
  }

  async ngOnInit() {
    setTimeout(() => {
      getLastFiveFFLs(this.sharedService);
    }, 500);
    if (this.accountingService.customers.length !== 0) {
      this.customers = this.accountingService.customers.filter(
        (customer) => customer.contact_type === 'Customer',
      );
    }
    this.addItems();
    this.itemsForm.get('acquisition_date').patchValue(this.acquisition_date);
  }
  get items(): FormArray {
    return this.itemsForm.get('items_to_acquire') as FormArray;
  }
  addItems(): void {
    if (this.acquisition_items.length !== 0) {
      this.acquisition_items.forEach((item) => {
        this.items.push(this.createItem(item));
      });
    } else {
      const blank_item: BulkAcquisitionItem = new BulkAcquisitionItem();
      blank_item.serial.push(null);
      this.items.push(this.createItem(blank_item));
    }
  }
  addNewItemClick(): void {
    const blank_item: BulkAcquisitionItem = new BulkAcquisitionItem();
    blank_item.serial.push(null);
    this.items.push(this.createItem(blank_item));
  }
  createItem(item: Item): FormGroup {
    return this.fb.group({
      description: [
        { value: item.description, disabled: true },
        Validators.required,
      ],
      upc: [
        { value: item.upc, disabled: this.importing_items },
        Validators.required,
      ],
      serial: this.fb.array(
        item.serial.map((serial) => this.createSerialGroup(serial)),
      ), // Initialize serial as a FormArray of FormGroups
    });
  }
  createSerialGroup(serial): FormGroup {
    return this.fb.group({
      serial: [serial, Validators.required],
    });
  }
  addSerial(index: number): void {
    const serialArray = this.items.at(index).get('serial') as FormArray;
    serialArray.push(this.createSerialGroup(null));
  }
  removeSerial(itemIndex: number, serialIndex: number): void {
    const serialArray = this.items.at(itemIndex).get('serial') as FormArray;
    serialArray.removeAt(serialIndex);
  }
  removeSerialCLick(itemIndex: number, serialIndex: number) {
    if (this.importing_items) {
      const optional_details = { itemIndex, serialIndex };
      this.sharedService.openYesNoModal(
        Questions.delete_serial,
        optional_details,
      );
    } else {
      this.removeSerial(itemIndex, serialIndex);
    }
  }
  removeItemClick(itemIndex: number): void {
    if (this.importing_items) {
      const optional_details = { itemIndex };
      this.sharedService.openYesNoModal(
        Questions.delete_bulk_serial_item,
        optional_details,
      );
    } else {
      this.removeItem(itemIndex);
    }
  }
  removeItem(index: number): void {
    this.items.removeAt(index);
  }
  getSerialArray(item: AbstractControl): FormArray {
    return (item as FormGroup).get('serial') as FormArray;
  }
  createSequentialSequence(index: number) {
    const serialArray = (this.itemsForm.get(['items_to_acquire']) as FormArray)
      .at(index)
      .get('serial') as FormArray;

    for (let i = 2; i < serialArray.value.length; i++) {
      const serialValue = serialArray.value[i];

      if (
        serialValue &&
        (serialValue.serial === null || serialValue.serial === '')
      ) {
        const value_a = serialArray.value[i - 2]?.serial;
        const value_b = serialArray.value[i - 1]?.serial;
        console.warn(value_a, value_b);

        if (!value_a || !value_b) {
          this.sharedService.raiseErrorToast(
            'Please fill out at least two previous serial numbers to generate a sequence.',
          );
          return;
        }

        try {
          const next_value = autoFill(value_a, value_b, 1);
          (
            (this.itemsForm.get(['items_to_acquire']) as FormArray)
              .at(index)
              .get('serial') as FormArray
          )
            .at(i)
            .patchValue({ serial: next_value });
        } catch (error: any) {
          this.sharedService.raiseErrorToast(
            error.message || 'Error generating sequence.',
          );
          return;
        }
      }
    }

    // for (let i = 0; i < ((this.itemsForm.get(['items_to_acquire']) as FormArray).at(index).get('serial') as FormArray).value.length; i++) {
    //   if (!((this.itemsForm.get(['items_to_acquire']) as FormArray).at(index).get('serial') as FormArray).value[i].serial) {
    //     const value_a = ((this.itemsForm.get(['items_to_acquire']) as FormArray).at(index).get('serial') as FormArray).value[i - 2].serial;
    //     const value_b = ((this.itemsForm.get(['items_to_acquire']) as FormArray).at(index).get('serial') as FormArray).value[i - 1].serial;
    //     const next_value = findCommonCharacters(value_a, value_b);
    //     if (next_value !== 'strings are not the same length') {
    //       ((this.itemsForm.get(['items_to_acquire']) as FormArray).at(index).get('serial') as FormArray).at(i).patchValue({ serial: next_value });
    //     }
    //   }
    // }
  }
  removeAllSerialsForGivenUpc(index: number) {
    const serialArray = this.items.at(index).get('serial') as FormArray;
    for (const control of serialArray.controls) {
      control.get('serial').patchValue(null);
    }
  }
  async addAllocationsClick() {
    this.sharedService.openStatusSpinner('Adding allocations');
    for (
      let i = 0;
      i < this.itemsForm.getRawValue().items_to_acquire.length;
      i++
    ) {
      for (
        let j = 0;
        j < this.itemsForm.getRawValue().items_to_acquire[i].serial.length;
        j++
      ) {
        await this.addAllocation(
          this.itemsForm.getRawValue().items_to_acquire[i].upc,
          this.itemsForm.getRawValue().items_to_acquire[i].serial[j]['serial'],
        );
      }
      this.sharedService.closeStatusSpinner();
    }
    setTimeout(() => {
      this.sharedService.closeStatusSpinner();
    }, 2000);
    this.sharedService.raiseSuccessToast('Allocations added successfully');
    if (this.importing_items) {
      this.router.navigate(['accounting-expense']);
    } else {
      this.router.navigate(['bound-book']);
    }
  }
  async addAllocation(firearm_upc: string, serial: string) {
    let selected_ffl: FFL = new FFL();
    let selected_customer: Customer = new Customer();
    let allocation: BoundBookEntry = new BoundBookEntry();
    const selected_firearm = this.getFirearmDetails(firearm_upc);
    allocation.firearm_id = this.findFirearmId();
    allocation.state = 'Available';
    let new_note = new Note();
    new_note.note_type = 'Acquisition';
    new_note.comment = 'Acquired';
    new_note.username = this.sharedService.logged_in_user.username;
    new_note.dateTime = format(new Date(), DateFormats.M_D_Y_H_M_AM);
    allocation.notes = [new_note];
    allocation.a_firearm_upc = firearm_upc;
    allocation.a_firearm_type = selected_firearm.firearm_type;
    allocation.a_serial_number = serial;
    allocation.a_manufacture = selected_firearm.manufacture;
    allocation.a_importer = selected_firearm.importer;
    allocation.a_model = selected_firearm.model;
    allocation.a_caliber_gauge = selected_firearm.caliber_gauge;
    allocation.a_additional_markings = selected_firearm.additional_markings;
    if (this.itemsForm.get('receiver_type').value === 'FFL') {
      selected_ffl = this.getFflDetails(
        this.itemsForm.get('received_from').value,
      );
      console.warn(selected_ffl);
      allocation.a_ffl = selected_ffl.ffl;
      allocation.a_business_name = selected_ffl.business_name;
      allocation.a_received_from_name = selected_ffl.legal_name;
      allocation.a_address = null;
      allocation.a_address2 = null;
      allocation.a_zip_code = null;
      allocation.a_state = null;
      allocation.a_city = null;
    } else {
      selected_customer = this.getCustomerDetails(
        this.itemsForm.get('received_from').value,
      );
      allocation.a_ffl = null;
      allocation.a_business_name = null;
      allocation.a_received_from_name =
        selected_customer.contact.name_first +
        ' ' +
        selected_customer.contact.name_last;
      allocation.a_address = selected_customer.address.street;
      allocation.a_address2 = selected_customer.address.street2;
      allocation.a_zip_code = selected_customer.address.zip_code;
      allocation.a_state = selected_customer.address.state;
      allocation.a_city = selected_customer.address.city;
    }
    allocation.a_date_acquired = this.itemsForm.get('acquisition_date').value;
    await this.sharedService.addBoundBookEntry(allocation);
    console.warn(allocation);
  }

  getFirearmDetails(firearm_upc: string) {
    console.warn(this.sharedService.firearm_list, firearm_upc);
    return this.sharedService.firearm_list.filter(
      (firearm) => firearm.firearm_upc === firearm_upc,
    )[0];
  }
  getFflDetails(ffl_id: string) {
    return this.sharedService.ffl_list.filter((ffl) => ffl.id === ffl_id)[0];
  }
  getCustomerDetails(customer_id: string) {
    return this.accountingService.customers.filter(
      (customer) => customer.id === customer_id,
    )[0];
  }
  closeClick() {
    if (this.acquisition_items.length !== 0) {
      this.sharedService.openYesNoModal(Questions.close_bulk_acquisition);
    } else {
      this.router.navigate(['expense']);
    }
  }
  openHome() {
    this.router.navigate(['home']);
  }
  findFirearmId(): number {
    if (this.sharedService.bound_book.length === 0) {
      return 0;
    } else {
      let max_firearm_id = this.sharedService.bound_book.reduce(
        (max, item) => (item.firearm_id > max ? item.firearm_id : max),
        this.sharedService.bound_book[0].firearm_id,
      );
      return max_firearm_id + 1;
    }
  }
  isSerialEmpty(itemIndex: number, serialIndex: number): boolean {
    const serialArray = this.items.at(itemIndex).get('serial') as FormArray;
    const serialControl = serialArray.at(serialIndex) as FormGroup;
    return !serialControl.get('serial').value;
  }
  isSerialNeedingHighlight(itemIndex: number, serialIndex: number): boolean {
    if (serialIndex === 0 || serialIndex === 1) {
      const serialArray = this.items.at(itemIndex).get('serial') as FormArray;
      if (serialArray.length > 2) {
        const serialControl = serialArray.at(serialIndex) as FormGroup;
        return !serialControl.get('serial').value;
      }
    }
    return false;
  }
  isUpcEmpty(index: number): boolean {
    const item = this.items.at(index) as FormGroup;
    const upcControl = item.get('upc');
    if (upcControl && upcControl.value && upcControl.value.trim() !== '') {
      return true;
    } else {
      return false;
    }
  }
  upcValueChanged(index: number, input: any) {
    //  this.items.at(index).patchValue({ description: '' });
    if (this.isUpcEmpty(index)) {
      // look for item in products list
      if (
        this.itemsForm.get('items_to_acquire').value[index].upc.length > 8 &&
        this.itemsForm.get('items_to_acquire').value[index].upc.length < 14
      ) {
        this.items.at(index).patchValue({ description: '' }); // removing description if upc has changed
        this.removeAllSerialsForGivenUpc(index); // removing serials if upc has changed

        this.accountingService.products_services.find((product) => {
          if (
            product.upc ===
              this.itemsForm.get('items_to_acquire').value[index].upc &&
            product.serial_item === false
          ) {
            this.sharedService.raiseInfoToast('This is not a serial item');
            setTimeout(() => {
              this.items.at(index).patchValue({ upc: '' });
              this.items.at(index).patchValue({ description: '' });
            }, 500);
          } else if (
            product.upc ===
              this.itemsForm.get('items_to_acquire').value[index].upc &&
            product.serial_item === true
          ) {
            this.itemsForm.get('items_to_acquire').value[index].description =
              product.description;
            this.items
              .at(index)
              .patchValue({ description: product.description });
          }
        });
      }
    } else {
      this.items.at(index).patchValue({ description: '' });
    }
  }
  upcValueSubmitted(index: number, input: any) {
    // clearing out the description  and all serials if the upc is empty
    if (this.isUpcEmpty(index)) {
      // look for item in products list
      const item_found = this.accountingService.products_services.filter(
        (product) =>
          product.upc ===
          this.itemsForm.get('items_to_acquire').value[index].upc.trim(),
      )[0];
      if (!item_found) {
        const item = new ProductServiceItem();
        item.type = InventoryTypes.inventory;
        item.name = this.itemsForm.get('items_to_acquire').value[index].upc;
        item.upc = this.itemsForm.get('items_to_acquire').value[index].upc;
        item.serial_item = true;
        item.preferred_vendor = this.itemsForm.get('received_from').value
          ? this.itemsForm.get('received_from').value
          : null;
        item.reorder_point = 0;
        item.income_asset_account = 'Inventory Asset';
        item.income_account = 'Merchandise Sales';
        item.expense_account = 'Cost of Goods Sold';
        item.inventory_asset_account = 'Inventory Asset';
        this.sharedService.openCreateProductService(
          ModalType.add,
          item,
          index,
          ComponentNames.bulk_acquisition,
        );
      } else {
        // I found in the product and service list patch the description
        this.accountingService.products_services.find((product) => {
          if (
            product.upc ===
            this.itemsForm.get('items_to_acquire').value[index].upc
          ) {
            this.itemsForm.get('items_to_acquire').value[index].description =
              product.description;
            this.items
              .at(index)
              .patchValue({ description: product.description });
          }
        });
        /// if not found in the Firearm quick selection list then add it
        const isInQuickSelection = this.isFirearmInQuickSelectionTable(
          this.itemsForm.get('items_to_acquire').value[index].upc,
        );
        if (!isInQuickSelection) {
          const firearm = new Firearm();
          firearm.firearm_upc =
            this.itemsForm.get('items_to_acquire').value[index].upc;
          console.warn(
            ModalType.add,
            firearm,
            index,
            ComponentNames.bulk_acquisition,
          );
          this.sharedService.addEditFirearmClick(
            ModalType.add,
            firearm,
            index,
            ComponentNames.bulk_acquisition,
          );
        }
      }
    }
  }

  isFirearmInQuickSelectionTable(upc: string): boolean {
    return (
      this.sharedService.firearm_list.filter(
        (firearm) => firearm.firearm_upc === upc,
      ).length > 0
    );
  }
  numbersOnly(event: any) {
    return numberOnly(event);
  }
  fflSearchClick() {
    this.sharedService.openFflSearchModal();
    console.log('Searching for a FFL');
  }
  compareFfls(ffl1: FFL, ffl2: FFL): boolean {
    return ffl1 && ffl2 ? ffl1.id === ffl2.id : ffl1 === ffl2;
  }
  compareCustomers(customer1: Customer, customer2: Customer): boolean {
    return customer1 && customer2
      ? customer1.id === customer2.id
      : customer1 === customer2;
  }
  switchToggled() {
    this.itemsForm.get('received_from').patchValue(null);
    if (this.isToggleOn) {
      this.itemsForm.get('receiver_type').patchValue('FFL');
    } else {
      this.itemsForm.get('receiver_type').patchValue(ContactTypes.customer);
    }
  }
  customerSearchClick() {
    //  this.sharedService.openCustomerSearchModal();
    console.log('Searching for a customer');
  }
  EditCustomerClick() {
    this.sharedService.openAddEditContactModal(
      ModalType.edit,
      ContactTypes.customer,
      this.accountingService.customers.filter(
        (customer) => customer.id === this.itemsForm.get('received_from').value,
      )[0],
      0,
    );
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

function findCommonCharacters(str1: string, str2: string): string {
  if (str1.length === str2.length) {
    let commonPrefix = '';
    for (let i = 0; i < str1.length; i++) {
      if (str1[i] === str2[i]) {
        commonPrefix += str1[i];
      } else {
        break;
      }
    }
    const str1_suffix = str1.slice(commonPrefix.length);
    const str2_suffix = str2.slice(commonPrefix.length);
    const suffix_delta = +str2_suffix - +str1_suffix;
    const next_value = +str2_suffix + +suffix_delta;
    return `${commonPrefix}${next_value}`;
  } else {
    return 'strings are not the same length';
  }
}

function isNumber(char: string): boolean {
  return /^[0-9]$/.test(char);
}

function autoFill(start1: string, start2: string, count: number): string {
  console.warn(start1, start2);

  const match1 = start1.match(/^(\D*)(\d+)$/);
  const match2 = start2.match(/^(\D*)(\d+)$/);
  console.warn(match1, match2);

  if (!match1 || !match2) {
    throw new Error(
      'Invalid input format (needs prefix and trailing number, or just numbers)',
    );
  }

  const prefix1 = match1[1];
  const prefix2 = match2[1];
  const number1 = parseInt(match1[2], 10);
  const number2 = parseInt(match2[2], 10);

  if (prefix1 !== prefix2 || number2 !== number1 + 1) {
    throw new Error('The provided values do not form a valid sequence');
  }

  // Preserve leading zeros by padding the resulting number to match the original length
  const numLength = match1[2].length;
  let currentNumber = number2 + count;

  return `${prefix1}${currentNumber.toString().padStart(numLength, '0')}`;
}

// Example usage:
//const filledValues = autoFill("abc123", "abc124", 10);
//console.log(filledValues); // Output: ["abc123", "abc124", "abc125", "abc126", "abc127", "abc128", "abc129", "abc130", "abc131", "abc132"]
class Item {
  constructor(
    public upc: string,
    public description: string,
    public serial: string[],
  ) {}
}
